import { isCdnEnabled } from '@myplayer/config';
import { GS1_AI } from '@myplayer/gs1';
import {
  type IdentifierRecord,
  type ScanAnalyticsDailyDocument,
  type ScanAnalyticsTopIdentifierItem,
  type ScanAnalyticsWorkspaceDailyDocument,
} from '@myplayer/schema';
import type { GetWorkspaceScanAnalyticsCallableResult } from './firebase-callables.js';

export function isAdminCdnEnabled(): boolean {
  const procEnv = (
    globalThis as { readonly process?: { readonly env?: Record<string, string | undefined> } }
  ).process?.env;
  const metaEnv = (
    import.meta as { readonly env?: Readonly<Record<string, string | boolean | undefined>> }
  ).env;
  const raw =
    procEnv?.['VITE_CDN_ENABLED'] ??
    procEnv?.['CDN_ENABLED'] ??
    String(metaEnv?.['VITE_CDN_ENABLED'] ?? metaEnv?.['CDN_ENABLED'] ?? '');
  return isCdnEnabled({ CDN_ENABLED: raw });
}

export {
  deriveNeedsAttentionItems,
  type NeedsAttentionCategory,
  type NeedsAttentionItem,
  type NeedsAttentionSummary,
} from './overview-needs-attention.js';

export type OverviewTimeWindow = 7 | 30 | 90;

export interface OverviewDayPoint {
  readonly day: string;
  readonly shortLabel: string;
  readonly scans: number;
  readonly heightPct: number;
}

export interface OverviewTopProductItem {
  readonly identifierKey: string;
  readonly workspaceId?: string;
  readonly workspaceName?: string;
  readonly title: string;
  readonly primaryKey: string;
  readonly ai: string;
  readonly scans: number;
  readonly sharePct: number;
}

export interface OverviewCountryItem {
  readonly countryCode: string;
  readonly countryLabel: string;
  readonly scans: number;
  readonly sharePct: number;
}

export interface OverviewDeviceSplitItem {
  readonly category: string;
  readonly label: string;
  readonly scans: number;
  readonly sharePct: number;
}

export interface AggregatedOverviewAnalytics {
  readonly windowDays: OverviewTimeWindow;
  readonly fromDay: string;
  readonly toDay: string;
  readonly totalScans: number;
  readonly cacheHits: number;
  readonly cacheMisses: number;
  readonly cacheHitRatePct: number;
  readonly averageDailyScans: number;
  readonly peakDay: { readonly day: string; readonly scans: number } | null;
  readonly series: readonly OverviewDayPoint[];
  readonly topProducts: readonly OverviewTopProductItem[];
  readonly topCountries: readonly OverviewCountryItem[];
  readonly deviceSplit: readonly OverviewDeviceSplitItem[];
}

const COUNTRY_NAME_MAP: Readonly<Record<string, string>> = Object.fromEntries(
  'GB:United Kingdom,US:United States,DE:Germany,FR:France,JP:Japan,NL:Netherlands,ES:Spain,IT:Italy,CA:Canada,AU:Australia,IE:Ireland,CH:Switzerland,SE:Sweden,KR:South Korea,SG:Singapore,ZZ:Unknown region'
    .split(',')
    .map((pair) => pair.split(':') as [string, string]),
);

const DEVICE_LABEL_MAP: Readonly<Record<string, string>> = Object.fromEntries(
  'mobile:Mobile phones,desktop:Desktop & laptop,tablet:Tablets,bot:Automated scanners & crawlers,other:Other devices'
    .split(',')
    .map((pair) => pair.split(':') as [string, string]),
);

const MONTH_NAMES = 'Jan,Feb,Mar,Apr,May,Jun,Jul,Aug,Sep,Oct,Nov,Dec'.split(',');

export function formatCountryCodeLabel(countryCode: string): string {
  const normalized = countryCode.trim().toUpperCase();
  const mapped = COUNTRY_NAME_MAP[normalized];
  return mapped !== undefined
    ? `${mapped} (${normalized})`
    : normalized.length === 2
      ? `Region ${normalized}`
      : 'Unknown region';
}

export function formatDeviceCategoryLabel(category: string): string {
  const normalized = category.trim().toLowerCase();
  return (
    DEVICE_LABEL_MAP[normalized] ?? `${normalized.charAt(0).toUpperCase()}${normalized.slice(1)}`
  );
}

export function formatShortUtcDayLabel(dayIso: string): string {
  const parsed = new Date(`${dayIso}T00:00:00.000Z`);
  if (Number.isNaN(parsed.getTime())) return dayIso;
  return `${MONTH_NAMES[parsed.getUTCMonth()] ?? ''} ${String(parsed.getUTCDate())}`;
}

export function getTodayUtcIsoDay(): string {
  return new Date().toISOString().slice(0, 10);
}

export function buildDayWindowList(
  windowDays: OverviewTimeWindow,
  referenceDay?: string,
): readonly string[] {
  const effectiveRef = referenceDay ?? getTodayUtcIsoDay();
  const baseDate = /^\d{4}-\d{2}-\d{2}$/.test(effectiveRef)
    ? new Date(`${effectiveRef}T00:00:00.000Z`)
    : new Date();
  const safeBase = Number.isNaN(baseDate.getTime()) ? new Date() : baseDate;

  const days: string[] = [];
  for (let offset = windowDays - 1; offset >= 0; offset -= 1) {
    const dt = new Date(
      Date.UTC(safeBase.getUTCFullYear(), safeBase.getUTCMonth(), safeBase.getUTCDate() - offset),
    );
    days.push(dt.toISOString().slice(0, 10));
  }
  return days;
}

function parseIdentifierKeyParts(identifierKey: string): {
  readonly ai: string;
  readonly primaryKey: string;
} {
  const idx = identifierKey.indexOf('_');
  return idx > 0
    ? { ai: identifierKey.slice(0, idx), primaryKey: identifierKey.slice(idx + 1) }
    : { ai: GS1_AI.GTIN, primaryKey: identifierKey };
}

function addPositiveCounts(
  target: Record<string, number>,
  source: Readonly<Record<string, number>> | undefined,
): void {
  for (const [k, v] of Object.entries(source ?? {})) {
    if (typeof v === 'number' && v > 0) target[k] = (target[k] ?? 0) + v;
  }
}

export function mergeWorkspaceScanAnalyticsResults(
  results: readonly GetWorkspaceScanAnalyticsCallableResult[],
): GetWorkspaceScanAnalyticsCallableResult {
  const first = results[0];
  let totalScans = 0;
  let cacheHits = 0;
  let cacheMisses = 0;
  const byDay: Record<string, number> = {};
  const byCountry: Record<string, number> = {};
  const byDeviceCategory: Record<string, number> = {};
  const byIdentifier: Record<string, number> = {};
  const topIdentifierMap = new Map<
    string,
    { ai: string; primaryKey: string; totalScans: number }
  >();

  for (const res of results) {
    totalScans += res.totalScans ?? 0;
    cacheHits += res.cacheHits ?? 0;
    cacheMisses += res.cacheMisses ?? 0;
    addPositiveCounts(byDay, res.byDay);
    addPositiveCounts(byCountry, res.byCountry);
    addPositiveCounts(byDeviceCategory, res.byDeviceCategory);
    addPositiveCounts(byIdentifier, res.byIdentifier);
    const items: readonly ScanAnalyticsTopIdentifierItem[] =
      res.topIdentifiers ??
      Object.entries(res.byIdentifier ?? {}).map(([identifierKey, scans]) => ({
        identifierKey,
        ...parseIdentifierKeyParts(identifierKey),
        totalScans: scans,
      }));
    for (const item of items) {
      if (item.totalScans > 0) {
        const parsed = parseIdentifierKeyParts(item.identifierKey);
        const existing = topIdentifierMap.get(item.identifierKey);
        topIdentifierMap.set(item.identifierKey, {
          ai: item.ai ?? existing?.ai ?? parsed.ai,
          primaryKey: item.primaryKey ?? existing?.primaryKey ?? parsed.primaryKey,
          totalScans: (existing?.totalScans ?? 0) + item.totalScans,
        });
      }
    }
  }

  const topIdentifiers: ScanAnalyticsTopIdentifierItem[] = Array.from(topIdentifierMap.entries())
    .map(([identifierKey, stat]) => ({
      identifierKey,
      ai: stat.ai,
      primaryKey: stat.primaryKey,
      totalScans: stat.totalScans,
    }))
    .sort((a, b) =>
      b.totalScans === a.totalScans
        ? a.identifierKey.localeCompare(b.identifierKey)
        : b.totalScans - a.totalScans,
    );

  return {
    orgId: first?.orgId ?? '',
    workspaceId: results.length === 1 ? (first?.workspaceId ?? '') : 'ALL',
    fromDay: first?.fromDay ?? '',
    toDay: first?.toDay ?? '',
    totalScans,
    cacheHits,
    cacheMisses,
    byIdentifier,
    byDay,
    byCountry,
    byDeviceCategory,
    topIdentifiers,
  };
}

export function aggregateOverviewScanAnalytics(params: {
  readonly rollupResult?: GetWorkspaceScanAnalyticsCallableResult | null;
  readonly workspaceDailyRollups?: readonly ScanAnalyticsWorkspaceDailyDocument[];
  readonly dailyRecords?: readonly ScanAnalyticsDailyDocument[];
  readonly identifiers: readonly IdentifierRecord[];
  readonly workspaceNameMap?: ReadonlyMap<string, string>;
  readonly windowDays: OverviewTimeWindow;
  readonly referenceDay?: string;
}): AggregatedOverviewAnalytics {
  const {
    rollupResult,
    workspaceDailyRollups,
    dailyRecords,
    identifiers,
    workspaceNameMap,
    windowDays,
  } = params;

  const anchorDay = params.referenceDay ?? getTodayUtcIsoDay();
  const dayList = buildDayWindowList(windowDays, anchorDay);
  const fromDay = dayList[0] ?? anchorDay;
  const toDay = dayList[dayList.length - 1] ?? anchorDay;

  const byDayMap = new Map<string, number>();
  for (const day of dayList) {
    byDayMap.set(day, 0);
  }

  let totalScans = 0;
  let cacheHits = 0;
  let cacheMisses = 0;
  const byIdentifierMap = new Map<string, { scans: number; primaryKey: string; ai: string }>();
  const byCountryMap = new Map<string, number>();
  const byDeviceMap = new Map<string, number>();

  if (rollupResult !== undefined && rollupResult !== null) {
    totalScans = rollupResult.totalScans ?? 0;
    cacheHits = rollupResult.cacheHits ?? 0;
    cacheMisses = rollupResult.cacheMisses ?? 0;
    for (const [day, count] of Object.entries(rollupResult.byDay ?? {})) {
      if (day >= fromDay && day <= toDay && typeof count === 'number' && count > 0) {
        byDayMap.set(day, (byDayMap.get(day) ?? 0) + count);
      }
    }
    const topList: readonly ScanAnalyticsTopIdentifierItem[] =
      rollupResult.topIdentifiers ??
      Object.entries(rollupResult.byIdentifier ?? {}).map(([identifierKey, scans]) => ({
        identifierKey,
        ...parseIdentifierKeyParts(identifierKey),
        totalScans: scans,
      }));
    for (const item of topList) {
      if (item.totalScans > 0) {
        const parsed = parseIdentifierKeyParts(item.identifierKey);
        byIdentifierMap.set(item.identifierKey, {
          scans: item.totalScans,
          primaryKey: item.primaryKey ?? parsed.primaryKey,
          ai: item.ai ?? parsed.ai,
        });
      }
    }
    for (const [country, count] of Object.entries(rollupResult.byCountry ?? {})) {
      if (typeof count === 'number' && count > 0) {
        byCountryMap.set(country, (byCountryMap.get(country) ?? 0) + count);
      }
    }
    for (const [device, count] of Object.entries(rollupResult.byDeviceCategory ?? {})) {
      if (typeof count === 'number' && count > 0) {
        byDeviceMap.set(device, (byDeviceMap.get(device) ?? 0) + count);
      }
    }
  } else {
    const sourceRecords = workspaceDailyRollups ?? dailyRecords ?? [];
    for (const rec of sourceRecords) {
      if (rec.day < fromDay || rec.day > toDay) continue;
      totalScans += rec.totalScans;
      cacheHits += rec.cacheHits;
      cacheMisses += rec.cacheMisses;
      byDayMap.set(rec.day, (byDayMap.get(rec.day) ?? 0) + rec.totalScans);

      if ('topIdentifiers' in rec && rec.topIdentifiers) {
        for (const [identKey, count] of Object.entries(rec.topIdentifiers)) {
          if (typeof count === 'number' && count > 0) {
            const parsed = parseIdentifierKeyParts(identKey);
            const prev = byIdentifierMap.get(identKey);
            byIdentifierMap.set(identKey, {
              scans: (prev?.scans ?? 0) + count,
              primaryKey: prev?.primaryKey ?? parsed.primaryKey,
              ai: prev?.ai ?? parsed.ai,
            });
          }
        }
      } else if ('identifierKey' in rec) {
        const prev = byIdentifierMap.get(rec.identifierKey);
        byIdentifierMap.set(rec.identifierKey, {
          scans: (prev?.scans ?? 0) + rec.totalScans,
          primaryKey: rec.primaryKey,
          ai: rec.ai,
        });
      }

      for (const [country, count] of Object.entries(rec.byCountry ?? {})) {
        if (typeof count === 'number' && count > 0) {
          byCountryMap.set(country, (byCountryMap.get(country) ?? 0) + count);
        }
      }
      for (const [device, count] of Object.entries(rec.byDeviceCategory ?? {})) {
        if (typeof count === 'number' && count > 0) {
          byDeviceMap.set(device, (byDeviceMap.get(device) ?? 0) + count);
        }
      }
    }
  }

  let maxDayScans = 0;
  let peakDay: { readonly day: string; readonly scans: number } | null = null;
  for (const day of dayList) {
    const count = byDayMap.get(day) ?? 0;
    if (count > maxDayScans) {
      maxDayScans = count;
      peakDay = { day, scans: count };
    }
  }

  const series: OverviewDayPoint[] = dayList.map((day) => {
    const scans = byDayMap.get(day) ?? 0;
    const heightPct =
      maxDayScans > 0 ? Math.max(scans > 0 ? 6 : 0, Math.round((scans / maxDayScans) * 100)) : 0;
    return { day, shortLabel: formatShortUtcDayLabel(day), scans, heightPct };
  });

  const identifierLookup = new Map<string, IdentifierRecord>();
  for (const ident of identifiers) identifierLookup.set(ident.identifierKey, ident);

  const topProducts: OverviewTopProductItem[] = Array.from(byIdentifierMap.entries())
    .map(([identifierKey, stat]) => {
      const matched = identifierLookup.get(identifierKey);
      const wsId = matched?.workspaceId;
      const wsName = wsId ? (workspaceNameMap?.get(wsId) ?? wsId) : undefined;
      const title =
        matched?.title ??
        matched?.draft?.title ??
        `Product ${matched?.primaryKey ?? stat.primaryKey}`;
      const sharePct = totalScans > 0 ? Math.round((stat.scans / totalScans) * 100) : 0;
      return {
        identifierKey,
        ...(wsId !== undefined ? { workspaceId: wsId } : {}),
        ...(wsName !== undefined ? { workspaceName: wsName } : {}),
        title,
        primaryKey: matched?.primaryKey ?? stat.primaryKey,
        ai: matched?.ai ?? stat.ai,
        scans: stat.scans,
        sharePct,
      };
    })
    .sort((a, b) =>
      b.scans === a.scans ? a.identifierKey.localeCompare(b.identifierKey) : b.scans - a.scans,
    )
    .slice(0, 5);

  const topCountries: OverviewCountryItem[] = Array.from(byCountryMap.entries())
    .map(([countryCode, scans]) => ({
      countryCode,
      countryLabel: formatCountryCodeLabel(countryCode),
      scans,
      sharePct: totalScans > 0 ? Math.round((scans / totalScans) * 100) : 0,
    }))
    .sort((a, b) =>
      b.scans === a.scans ? a.countryCode.localeCompare(b.countryCode) : b.scans - a.scans,
    )
    .slice(0, 5);

  const totalDeviceScans = Array.from(byDeviceMap.values()).reduce((acc, v) => acc + v, 0);
  const deviceDenominator = totalDeviceScans > 0 ? totalDeviceScans : totalScans;
  const deviceSplit: OverviewDeviceSplitItem[] = Array.from(byDeviceMap.entries())
    .map(([category, scans]) => ({
      category,
      label: formatDeviceCategoryLabel(category),
      scans,
      sharePct: deviceDenominator > 0 ? Math.round((scans / deviceDenominator) * 100) : 0,
    }))
    .sort((a, b) =>
      b.scans === a.scans ? a.category.localeCompare(b.category) : b.scans - a.scans,
    );

  return {
    windowDays,
    fromDay,
    toDay,
    totalScans,
    cacheHits,
    cacheMisses,
    cacheHitRatePct: totalScans > 0 ? Math.round((cacheHits / totalScans) * 100) : 0,
    averageDailyScans: totalScans > 0 ? Math.round(totalScans / windowDays) : 0,
    peakDay,
    series,
    topProducts,
    topCountries,
    deviceSplit,
  };
}
